import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const GOLD = '#B8860B';

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setLoading(true);
      // 1. Get user role from Firestore
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const role = userSnap.data()?.role || 'client';

      // 2. Query alerts for this specific role or everyone
      const q = query(
        collection(db, 'system_alerts'),
        where('target', 'in', ['all', role === 'stylist' ? 'stylists' : 'clients']),
        orderBy('createdAt', 'desc')
      );

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(data);

      // 3. Mark as read: Save the current time as the last time they checked the inbox
      // This is used by the Sidebar badge logic to show "0" new messages
      localStorage.setItem('lastReadAlert', Date.now().toString());
      
    } catch (err) {
      console.error("Error loading inbox:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    if (window.confirm("This will hide current messages from your view. Proceed?")) {
      localStorage.setItem('lastReadAlert', Date.now().toString());
      setMessages([]);
    }
  };

  return (
    <div style={container}>
      <header style={header}>
        <div>
          <h1 style={{ color: GOLD, margin: 0 }}>System Inbox</h1>
          <p style={{ opacity: 0.6, fontSize: '0.9rem' }}>Official announcements and platform updates</p>
        </div>
        {messages.length > 0 && (
          <button onClick={clearHistory} style={clearBtn}>Clear View</button>
        )}
      </header>

      {loading ? (
        <div style={emptyState}>
          <p>Scanning for updates...</p>
        </div>
      ) : messages.length === 0 ? (
        <div style={emptyState}>
            <span style={{ fontSize: '3rem', display: 'block', marginBottom: '10px' }}>📩</span>
            <p>Your inbox is empty.</p>
            <p style={{ fontSize: '0.8rem', opacity: 0.5 }}>New broadcasts will appear here.</p>
        </div>
      ) : (
        <div style={listStyle}>
          {messages.map((msg) => (
            <div key={msg.id} style={msgCard}>
              <div style={msgHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={typeBadge}>{msg.target?.toUpperCase() || 'GLOBAL'}</span>
                  {msg.priority === 'high' && <span style={priorityBadge}>URGENT</span>}
                </div>
                <span style={dateStyle}>
                  {msg.createdAt?.toDate().toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              <p style={msgBody}>{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// STYLES
const container = { 
  padding: '2rem', 
  maxWidth: '800px', 
  margin: '0 auto', 
  minHeight: '100vh', 
  color: 'white',
  background: 'transparent' // Inherits from main layout
};

const header = { 
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid rgba(184, 134, 11, 0.2)`, 
  paddingBottom: '1.5rem', 
  marginBottom: '2rem' 
};

const clearBtn = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.2)',
  color: 'white',
  padding: '8px 16px',
  borderRadius: '8px',
  fontSize: '0.75rem',
  cursor: 'pointer',
  transition: '0.2s'
};

const listStyle = { 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '15px' 
};

const msgCard = { 
  background: 'rgba(255,255,255,0.03)', 
  padding: '20px', 
  borderRadius: '16px', 
  border: '1px solid rgba(255,255,255,0.05)',
  transition: 'transform 0.2s ease',
  cursor: 'default'
};

const msgHeader = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  marginBottom: '12px', 
  alignItems: 'center' 
};

const typeBadge = { 
  fontSize: '0.6rem', 
  color: GOLD, 
  border: `1px solid ${GOLD}`, 
  padding: '2px 8px', 
  borderRadius: '4px', 
  fontWeight: 'bold',
  letterSpacing: '1px'
};

const priorityBadge = {
  fontSize: '0.6rem',
  background: '#ff4d4d',
  color: 'white',
  padding: '2px 8px',
  borderRadius: '4px',
  fontWeight: 'bold'
};

const dateStyle = { 
  fontSize: '0.75rem', 
  opacity: 0.4 
};

const msgBody = { 
  margin: 0, 
  fontSize: '0.95rem', 
  lineHeight: '1.6', 
  opacity: 0.9 
};

const emptyState = { 
  textAlign: 'center', 
  padding: '100px 0', 
  opacity: 0.4,
  background: 'rgba(255,255,255,0.01)',
  borderRadius: '24px',
  border: '1px dashed rgba(255,255,255,0.1)'
};
